/* MQS all-brand thread-gauge search — 27 September 2026.
 * Reads public Shopify results only. Never changes products, publishing, carts or prices.
 * Every feed page is checked before numerical sorting and customer pagination.
 * Brands are deliberately NOT used as an inclusion/exclusion filter.
 */
(function (global) {
  'use strict';
  const VERSION = '2026-10-22-plain-ring-sku-fix';
  const MAX_PAGES = 100; // 5,000 feed matches. Fail explicitly rather than silently truncate.
  const CACHE_MS = 60000;
  const FEED_VIEW = 'mqs-thread-data';
  const REQUEST_MS = 12000;
  const CATALOG_QUERY = '(product_type:"Thread Gauges" OR product_type:"Thread Measurement" OR title:thread* OR title:screw* OR vendor:Leitech OR title:multicheck* OR title:"multi-check")';
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
  let catalogCache = null;
  // Two exact, owner-confirmed aliases. Do not decode arbitrary SKUs as dimensions.
  // An alias does NOT publish its product or create a search-result card.
  const ALIASES = Object.freeze({
    'M47P': { diameter: 4, pitch: 0.7, kind: 'plug' },
    'P9023-M4-1': { diameter: 4, pitch: 0.7, kind: 'plug' }
  });
  const number = value => Number(String(value).replace(',', '.'));
  const clean = value => String(value || '').replace(/\u00a0/g, ' ').replace(/[−–—]/g, '-').trim();
  const canonical = value => clean(value).toUpperCase().replace(/\s+/g, '');
  function queryText(query) {
    return clean(query).replace(/^product_type:(?:"[^"]+"|.*?)\s+AND\s+/i, '').trim();
  }
  function metric(diameter, pitch) {
    return { diameter, pitch, label: 'M' + diameter + (pitch == null ? '' : ' × ' + pitch) };
  }
  function explicitSizes(text) {
    const result = [];
    const re = /(?:^|[^a-z0-9])M\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?|[.,]\d+)(?![\d.,])/gi;
    for (const match of clean(text).matchAll(re)) {
      const d = number(match[1]), p = number(match[2]);
      if (d > 0 && p > 0 && Number.isFinite(d) && Number.isFinite(p)) result.push(metric(d, p));
    }
    return result;
  }
  function parseMetricSize(text) {
    const sizes = explicitSizes(text);
    if (sizes.length) return sizes[0];
    // M47P, P9023-M4-1 and M3-M12 are NOT standalone diameters.
    const match = clean(text).match(/(?:^|[\s:(])M\s*(\d+(?:[.,]\d+)?)(?=$|[\s;),])/i);
    return match && number(match[1]) > 0 ? metric(number(match[1]), null) : null;
  }
  function kindOf(record) {
    const text = (record.title || '') + ' ' + (record.type || '');
    if (/\b(?:rings?|ngr|gr)\b/i.test(text)) return 'ring';
    if (/\bplugs?\b|multi[ -]?check|leitech|thread.{0,20}depth/i.test(text)) return 'plug';
    return 'other';
  }
  function isThreadGauge(record) {
    const title = String(record.title || ''), type = String(record.type || '');
    if (/\bplain[ -]+(?:rings?|plugs?)\b/i.test(title + ' ' + type)) return false;
    // Screw-thread micrometers, styli and fixtures must remain in ordinary search.
    if (/\b(?:micrometers?|microscope|stylus|styli|spindle|screwdriver|adaptor|adapter)\b/i.test(title) && !/\b(?:plug|ring)\s+ga(?:u)?ges?\b/i.test(title)) return false;
    const threaded = /\b(?:thread\w*|screw\w*)\b/i.test(title + ' ' + type);
    const gauge = /\b(?:gauges?|gages?|plugs?|rings?)\b/i.test(title);
    const knownDepthGauge = /leitech|multi[ -]?check/i.test(title + ' ' + record.vendor) && gauge;
    const metricPlugRing = /\b(?:metric|unc|unf|unef|bsw|bsf|bsp|npt)\b/i.test(title) && /\b(?:plug|ring)\b/i.test(title) && gauge;
    return (threaded && gauge) || knownDepthGauge || metricPlugRing;
  }
  function prepareRecord(record) {
    const item = Object.assign({}, record);
    item.skus = Array.isArray(item.skus) ? item.skus.filter(Boolean).map(String) : [];
    const variants = Array.isArray(item.variantTitles) ? item.variantTitles : [];
    let sizes = explicitSizes(item.title);
    variants.forEach(title => sizes.push(...explicitSizes(title)));
    if (!sizes.length) {
      for (const code of Object.keys(ALIASES)) {
        if (item.skus.some(sku => canonical(sku) === canonical(code)) || tokenIn(item.title, code)) {
          sizes.push(metric(ALIASES[code].diameter, ALIASES[code].pitch)); break;
        }
      }
    }
    if (!sizes.length) {
      const found = parseMetricSize(item.title);
      if (found) sizes.push(found);
    }
    const seen = new Set();
    item.sizes = sizes.filter(size => {
      const key = size.diameter + '/' + size.pitch;
      if (seen.has(key)) return false;
      seen.add(key); return true;
    }).sort((a, b) => a.diameter - b.diameter || (a.pitch || 0) - (b.pitch || 0));
    item.metric = item.sizes[0] || null;
    item.kind = kindOf(item);
    return item;
  }
  function tokenIn(text, token) {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp('(?:^|[^a-z0-9])' + escaped + '(?=$|[^a-z0-9])', 'i').test(text);
  }
  function exactRecord(query, records) {
    const q = queryText(query), key = canonical(q);
    if (!/[0-9]/.test(key) || /\s/.test(q)) return null;
    return records.find(record => isThreadGauge(record) && (
      (record.skus || []).some(sku => canonical(sku) === key) || tokenIn(record.title, q)
    )) || null;
  }
  function aliasFor(query) {
    const q = queryText(query);
    for (const code of Object.keys(ALIASES)) if (tokenIn(q, code)) return { code, ...ALIASES[code] };
    return null;
  }
  function isThreadQuery(query, seeds) {
    const q = queryText(query);
    if (!q) return false;
    // Plain ring/plug requests belong to ordinary product search.
    if (/\bplain[ -]+(?:rings?|plugs?)\b/i.test(q) || /^p9026(?:-|$)/i.test(q)) return false;
    if (/\b(?:micrometers?|microscope|stylus|styli|spindle|screwdriver|adaptor|adapter)\b/i.test(q) && !/\b(?:plug|ring)\s+ga(?:u)?ges?\b/i.test(q)) return false;
    if (aliasFor(q)) return true;
    const queryAsRecord = { title: q, type: '', vendor: '' };
    if (isThreadGauge(queryAsRecord)) return true;
    if (/\b(?:leitech|multi[ -]?check|threadmaster)\b/i.test(q)) return true;
    if (/\b(?:pride|insize)\b/i.test(q) && /\b(?:thread|screw|plugs?|rings?)\b/i.test(q)) return true;
    // Plain metric requests such as M4, M4x.7, or M4 x 0.7 activate the size browser.
    if (/^m\s*\d+(?:[.,]\d+)?(?:\s*[x×]\s*(?:\d+(?:[.,]\d+)?|[.,]\d+))?$/i.test(q)) return true;
    if (parseMetricSize(q) && /\b(?:gauges?|gages?|plugs?|rings?|thread|screw)\b/i.test(q)) return true;
    // Keep confirmed thread-code aliases above; never infer thread classification from a PRIDE SKU prefix.
    if (/^m\d+(?:[.,]\d+)?(?:p|gr|ngr)$/i.test(q)) return true;
    return !!(seeds && exactRecord(q, seeds));
  }
  function intentFor(query, products, seeds) {
    const q = queryText(query);
    const alias = aliasFor(q);
    const exact = exactRecord(q, products) || exactRecord(q, seeds || []);
    const size = explicitSizes(q)[0] || (alias ? metric(alias.diameter, alias.pitch) : null) ||
      (exact && exact.metric) || parseMetricSize(q);
    let kind = /\brings?\b/i.test(q) ? 'ring' : /\bplugs?\b/i.test(q) ? 'plug' : '';
    if (!kind && alias) kind = alias.kind;
    if (!kind && exact && exact.metric) kind = exact.kind === 'other' ? '' : exact.kind;
    let remaining = q;
    if (alias) remaining = remaining.replace(new RegExp(alias.code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'ig'), ' ');
    if (exact && exact.metric) remaining = '';
    remaining = remaining.replace(/\bM\s*\d+(?:[.,]\d+)?\s*[x×]\s*(?:\d+(?:[.,]\d+)?|[.,]\d+)/ig, ' ');
    if (size) remaining = remaining.replace(/\bM\s*\d+(?:[.,]\d+)?(?=$|[\s;),])/ig, ' ');
    // A brand term describes the starting search, not an all-brand exclusion rule.
    const brands = new Set(['pride', 'leitech', 'multicheck', 'multi-check', 'multi check', 'insize', 'threadmaster', 'johs-boss', 'johs boss', 'jbo']);
    products.forEach(record => { if (record.vendor) brands.add(String(record.vendor).toLowerCase()); });
    Array.from(brands).sort((a, b) => b.length - a.length).forEach(brand => {
      if (!brand) return;
      const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      remaining = remaining.replace(new RegExp('\\b' + escaped + '\\b', 'ig'), ' ');
    });
    const metricOnly = /\bmetric\b/i.test(remaining);
    remaining = remaining.replace(/\b(?:all|brands?|thread\w*|screw\w*|gauges?|gages?|plugs?|rings?|metric|size|sizes|pitch|pitches|find|search|for|and|the|in|order|by)\b/ig, ' ');
    const terms = remaining.toLowerCase().match(/[a-z0-9][a-z0-9./-]*/g) || [];
    return { size: size ? String(size.diameter) : '', pitch: size && size.pitch != null ? String(size.pitch) : '', kind, terms, metricOnly };
  }
  function matchesTerms(record, intent) {
    if (intent.metricOnly && !record.metric) return false;
    const haystack = (record.title + ' ' + record.type + ' ' + record.skus.join(' ')).toLowerCase();
    return intent.terms.every(term => haystack.includes(term));
  }
  function matchesSize(record, size, pitch) {
    if (size === 'other') return !record.sizes.length;
    if (!size && !pitch) return true;
    return record.sizes.some(item => (!size || String(item.diameter) === size) && (!pitch || (item.pitch != null && String(item.pitch) === pitch)));
  }
  function compareSize(a, b, descending) {
    const first = a.metric, second = b.metric;
    if (first && !second) return -1;
    if (!first && second) return 1;
    if (first && second) {
      const diameter = first.diameter - second.diameter;
      if (diameter) return descending ? -diameter : diameter;
      const p1 = first.pitch == null ? Infinity : first.pitch, p2 = second.pitch == null ? Infinity : second.pitch;
      if (p1 !== p2) return p1 < p2 ? -1 : 1;
    }
    return collator.compare(a.vendor || '', b.vendor || '') || collator.compare(a.title, b.title) || collator.compare(String(a.id), String(b.id));
  }
  function sortRecords(records, mode) {
    return records.slice().sort((a, b) => {
      if (mode === 'price-ascending' || mode === 'price-descending') {
        if (!a.price && b.price) return 1;
        if (a.price && !b.price) return -1;
        const difference = a.price - b.price;
        if (difference) return mode === 'price-descending' ? -difference : difference;
      }
      return compareSize(a, b, mode === 'size-descending');
    });
  }
  function searchPath() {
    return (global.routes && global.routes.searchUrl) || (((global.Shopify || {}).routes || {}).root || '/') + 'search';
  }
  function addPreview(url) {
    const current = new URL(global.location.href);
    let preview = current.searchParams.get('preview_theme_id');
    const theme = (global.Shopify || {}).theme;
    if (!preview && theme && theme.id && theme.role && theme.role !== 'main') preview = String(theme.id);
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  function makeFeedUrl(page, transport, pageParam) {
    // Isolated alternate template avoids competition with the main search's paginate block.
    // section_id remains a separately validated compatibility route, not the sole dependency.
    const url = addPreview(new URL(searchPath(), global.location.origin));
    url.searchParams.set('q', CATALOG_QUERY);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'show');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('sort_by', 'title-ascending');
    if (transport === 'section') url.searchParams.set('section_id', 'mqs-thread-search-data');
    else url.searchParams.set('view', FEED_VIEW);
    url.searchParams.set(pageParam || 'page', String(page));
    return url;
  }
  function arrayData(value) {
    if (!value) return [];
    const array = JSON.parse(value);
    if (!Array.isArray(array)) throw new Error('Invalid product variant data');
    return array;
  }
  function fromTemplate(template) {
    const data = template.dataset;
    const price = Number(data.price || 0);
    if (!data.id || !data.title || !Number.isFinite(price)) throw new Error('Incomplete thread gauge data');
    return prepareRecord({ id: data.id, vendor: data.vendor || '', title: data.title, type: data.type || '',
      skus: arrayData(data.skus), variantTitles: arrayData(data.variantTitles), price, url: data.url || '', image: data.image || '', template });
  }
  function parseFeed(text, page, transport) {
    const document = new DOMParser().parseFromString(text, 'text/html');
    const feed = document.querySelector('[data-mqs-thread-feed]');
    if (!feed) throw new Error('Thread gauge data was not returned (' + transport + ')');
    const currentPage = Number(feed.dataset.page);
    const pages = Math.max(1, Number(feed.dataset.pages));
    const total = Number(feed.dataset.total);
    const pageSize = Number(feed.dataset.pageSize || 50);
    if (!feed.hasAttribute('data-total') || !feed.hasAttribute('data-pages') ||
        currentPage !== page || !Number.isInteger(pages) || !Number.isInteger(total) || total < 0 ||
        !Number.isInteger(pageSize) || pageSize < 1 || pageSize > 50 ||
        pages !== Math.max(1, Math.ceil(total / pageSize))) {
      throw new Error('Thread gauge pagination could not be verified (' + transport + ', page ' + page + ')');
    }
    if (feed.hasAttribute('data-query') && feed.dataset.query !== CATALOG_QUERY) {
      throw new Error('The data request returned a different search');
    }
    if (pages > MAX_PAGES) {
      const error = new Error('The catalogue exceeds the size-browser safety limit');
      error.tooLarge = true; throw error;
    }
    const raw = Array.from(feed.querySelectorAll('template[data-mqs-record]')).map(fromTemplate);
    const expected = Math.max(0, Math.min(pageSize, total - (page - 1) * pageSize));
    if (raw.length !== expected) throw new Error('Incomplete thread gauge page (' + page + ': ' + raw.length + '/' + expected + ')');
    const pageParam = feed.dataset.pageParam || 'page';
    if (!/^page(?:_[a-z0-9]+)?$/i.test(pageParam)) throw new Error('Unrecognised pagination parameter');
    return { page, pages, total, pageSize, pageParam, raw, transport };
  }
  async function requestPage(page, transport, pageParam, signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      if (signal && signal.aborted) throw new Error('Catalogue request cancelled');
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (signal) signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, REQUEST_MS);
      try {
        const response = await global.fetch(makeFeedUrl(page, transport, pageParam).toString(), {
          method: 'GET', credentials: 'same-origin', signal: controller.signal,
          headers: { Accept: 'text/html' }
        });
        if (!response.ok) {
          const error = new Error('Thread gauge data HTTP ' + response.status + ' (' + transport + ')');
          error.noRetry = response.status >= 400 && response.status < 500 && response.status !== 429;
          throw error;
        }
        return parseFeed(await response.text(), page, transport);
      } catch (error) {
        if (attempt === 1 || error.tooLarge || error.noRetry || (signal && signal.aborted)) throw error;
        await new Promise(resolve => setTimeout(resolve, 350));
      } finally {
        clearTimeout(timeout);
        if (signal) signal.removeEventListener('abort', abort);
      }
    }
    throw new Error('Thread gauges could not be loaded');
  }
  async function loadTransport(transport, signal) {
    const first = await requestPage(1, transport, 'page', signal);
    const batches = new Array(first.pages); batches[0] = first;
    let nextPage = 2;
    async function worker() {
      while (nextPage <= first.pages) {
        const page = nextPage++, batch = await requestPage(page, transport, first.pageParam, signal);
        if (batch.total !== first.total || batch.pages !== first.pages || batch.pageSize !== first.pageSize) {
          throw new Error('The catalogue changed while loading');
        }
        batches[page - 1] = batch;
      }
    }
    await Promise.all(Array.from({ length: Math.min(2, Math.max(0, first.pages - 1)) }, worker));
    const seen = new Set(), records = [];
    batches.forEach(batch => {
      if (!batch) throw new Error('A search page is missing');
      batch.raw.forEach(record => {
        if (seen.has(record.id)) throw new Error('Duplicate product across search pages');
        seen.add(record.id);
        if (isThreadGauge(record)) records.push(record);
      });
    });
    if (seen.size !== first.total) throw new Error('The complete product list could not be verified');
    return sortRecords(records, 'size-ascending');
  }
  async function loadAllPages() {
    const failures = [];
    for (const transport of ['view', 'section']) {
      const controller = new AbortController();
      // One stalled request must not keep search indefinitely busy.
      const deadline = setTimeout(() => controller.abort(), 30000);
      try {
        return await loadTransport(transport, controller.signal);
      } catch (error) {
        failures.push(transport + ': ' + error.message);
        if (error.tooLarge) throw error;
      } finally {
        clearTimeout(deadline); controller.abort();
      }
    }
    throw new Error(failures.join(' | '));
  }
  function getCatalog(refresh) {
    if (!refresh && catalogCache && (!catalogCache.settled || Date.now() - catalogCache.time < CACHE_MS)) return catalogCache.promise;
    const entry = { time: Date.now(), settled: false, promise: null };
    entry.promise = loadAllPages().then(records => { entry.time = Date.now(); entry.settled = true; return records; }).catch(error => {
      if (catalogCache === entry) catalogCache = null; throw error;
    });
    catalogCache = entry; return entry.promise;
  }
  async function getProducts(query, refresh, seeds) {
    const products = await getCatalog(refresh), intent = intentFor(query, products, seeds || []);
    return products.filter(record => matchesTerms(record, intent) && matchesSize(record, intent.size, intent.pitch) && (!intent.kind || record.kind === intent.kind));
  }
  function element(tag, className, text) {
    const node = global.document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }
  function allResultsButton() {
    const button = element('button', 'search-bar__view-all', 'View matching thread gauges — all brands'); button.type = 'submit'; return button;
  }
  async function suggestions(query) {
    const products = await getProducts(query);
    const fragment = global.document.createDocumentFragment();
    fragment.appendChild(element('p', 'search-bar__result-category', 'Thread gauges · All brands · Size order'));
    const list = element('div', 'search-bar__result-products');
    products.slice(0, 3).forEach(record => {
      const link = element('a', 'search-bar__result-item'); link.href = addPreview(new URL(record.url, global.location.origin)).toString();
      if (record.image) {
        const container = element('div', 'search-bar__image-container'), ratio = element('div', 'aspect-ratio aspect-ratio--square'), image = element('img');
        image.src = record.image; image.alt = record.title; ratio.appendChild(image); container.appendChild(ratio); link.appendChild(container);
      }
      const info = element('div', 'search-bar__item-info');
      info.appendChild(element('p', 'search-bar__item-title', (record.metric ? record.metric.label + ' — ' : '') + record.title));
      const price = record.template.content.querySelector('[data-mqs-price-html]');
      if (price) info.appendChild(element('span', 'search-bar__item-price', price.textContent.replace(/\s+/g, ' ').trim()));
      link.appendChild(info); list.appendChild(link);
    });
    if (!products.length) list.appendChild(element('p', 'search-bar__empty-state', 'No matching published thread gauges.'));
    fragment.appendChild(list); fragment.appendChild(allResultsButton()); return fragment;
  }
  function suggestionError() {
    const fragment = global.document.createDocumentFragment();
    fragment.appendChild(element('p', 'search-bar__empty-state', 'Press Search to browse thread gauges by size.'));
    fragment.appendChild(allResultsButton()); return fragment;
  }
  function mount(panel) {
    if (panel.dataset.mqsMounted || new URL(global.location.href).searchParams.get('mqs_standard') === '1') return;
    const seeds = Array.from(panel.querySelectorAll('template[data-mqs-seed]')).map(fromTemplate);
    const query = panel.dataset.query;
    if (!isThreadQuery(query, seeds)) return;
    panel.dataset.mqsMounted = 'true';
    const native = panel.parentElement.querySelector('[data-mqs-native-search]');
    if (native) native.hidden = false;
    panel.hidden = false;
    const grid = panel.querySelector('[data-mqs-grid]'), status = panel.querySelector('[data-mqs-status]'), controls = panel.querySelector('[data-mqs-controls]');
    const pager = panel.querySelector('[data-mqs-pagination]'), retry = panel.querySelector('[data-mqs-retry]'), fallback = panel.querySelector('[data-mqs-fallback]');
    const sizeSelect = panel.querySelector('[data-mqs-size]'), pitchSelect = panel.querySelector('[data-mqs-pitch]'), kindSelect = panel.querySelector('[data-mqs-kind]');
    const sortSelect = panel.querySelector('[data-mqs-sort]'), pageSelect = panel.querySelector('[data-mqs-per-page]');
    let products = [], intent = null, loaded = false, generation = 0;
    let state = { size: '', pitch: '', kind: '', sort: 'size-ascending', perPage: 24, page: 1, all: false };
    function availableProducts() { return state.all ? products : products.filter(record => matchesTerms(record, intent)); }
    function appendOption(select, value, text) { const option = element('option', '', text); option.value = value; select.appendChild(option); }
    function syncSelect(select, value, label) {
      if (value && !Array.from(select.options).some(option => option.value === value)) appendOption(select, value, label + ' (no matches)');
      select.value = value;
    }
    function populateFilters() {
      const available = availableProducts();
      const diameters = Array.from(new Set(available.flatMap(record => record.sizes.map(size => size.diameter)))).sort((a, b) => a - b);
      sizeSelect.replaceChildren(); appendOption(sizeSelect, '', 'All sizes');
      diameters.forEach(d => appendOption(sizeSelect, String(d), 'M' + d));
      if (available.some(record => !record.metric) || state.size === 'other') appendOption(sizeSelect, 'other', 'Imperial / other sizes');
      syncSelect(sizeSelect, state.size, 'M' + state.size);
      const pitches = Array.from(new Set(available.flatMap(record => record.sizes.filter(size => (!state.size || String(size.diameter) === state.size) && size.pitch != null).map(size => size.pitch)))).sort((a, b) => a - b);
      pitchSelect.replaceChildren(); appendOption(pitchSelect, '', 'All pitches');
      pitches.forEach(p => appendOption(pitchSelect, String(p), p + ' mm'));
      syncSelect(pitchSelect, state.pitch, state.pitch + ' mm');
      pitchSelect.disabled = state.size === 'other';
      kindSelect.value = state.kind; sortSelect.value = state.sort; pageSelect.value = String(state.perPage);
    }
    function readState() {
      const params = new URL(global.location.href).searchParams;
      const all = params.get('mqs_all') === '1';
      const validModes = ['size-ascending', 'size-descending', 'price-ascending', 'price-descending'];
      state = {
        size: params.has('mqs_size') ? params.get('mqs_size') : (all ? '' : intent.size),
        pitch: params.has('mqs_pitch') ? params.get('mqs_pitch') : (all ? '' : intent.pitch),
        kind: params.has('mqs_kind') ? params.get('mqs_kind') : (all ? '' : intent.kind),
        sort: validModes.includes(params.get('mqs_sort')) ? params.get('mqs_sort') : 'size-ascending',
        perPage: [24, 48, 96].includes(Number(params.get('mqs_per_page'))) ? Number(params.get('mqs_per_page')) : 24,
        page: Math.max(1, parseInt(params.get('mqs_page') || '1', 10) || 1), all
      };
      if (!['', 'plug', 'ring', 'other'].includes(state.kind)) state.kind = '';
      if (state.size === 'other') state.pitch = '';
      if (state.size && state.size !== 'other' && !(Number(state.size) > 0)) state.size = '';
      if (state.pitch && !(Number(state.pitch) > 0)) state.pitch = '';
      populateFilters();
    }
    function pageUrl(page) {
      const url = addPreview(new URL(global.location.href));
      ['page', 'sort_by', 'section_id', 'sections', 'view', 'mqs_standard'].forEach(key => url.searchParams.delete(key));
      // Native search facets cannot silently narrow an all-brand feed.
      Array.from(url.searchParams.keys()).filter(key => key.startsWith('filter.')).forEach(key => url.searchParams.delete(key));
      url.searchParams.set('q', query); url.searchParams.set('type', 'product');
      // Empty values are intentional: changing M4 to All must survive refresh/back.
      const fields = { mqs_size: state.size, mqs_pitch: state.pitch, mqs_kind: state.kind, mqs_sort: state.sort,
        mqs_per_page: String(state.perPage), mqs_page: String(page), mqs_all: state.all ? '1' : '0' };
      Object.keys(fields).forEach(key => url.searchParams.set(key, fields[key])); return url;
    }
    function updateUrl() { global.history.pushState({ mqsThreadSearch: true }, '', pageUrl(state.page).toString()); }
    function render() {
      if (!loaded) return;
      const matching = availableProducts().filter(record => matchesSize(record, state.size, state.pitch) && (!state.kind || record.kind === state.kind));
      const sorted = sortRecords(matching, state.sort), pages = Math.max(1, Math.ceil(sorted.length / state.perPage));
      state.page = Math.max(1, Math.min(state.page, pages));
      const start = (state.page - 1) * state.perPage, visible = sorted.slice(start, start + state.perPage), fragment = global.document.createDocumentFragment();
      visible.forEach(record => {
        const card = record.template.content.cloneNode(true), label = card.querySelector('[data-mqs-size-label]');
        const matchingSizes = record.sizes.filter(size => (!state.size || String(size.diameter) === state.size) && (!state.pitch || String(size.pitch) === state.pitch));
        if (label && record.sizes.length) { label.textContent = (matchingSizes.length ? matchingSizes : record.sizes).map(size => size.label).join(' / '); label.hidden = false; }
        card.querySelectorAll('a[href]').forEach(link => { link.href = addPreview(new URL(link.getAttribute('href'), global.location.origin)).toString(); });
        fragment.appendChild(card);
      });
      grid.replaceChildren(fragment);
      const sortNames = { 'size-ascending': 'Size: small to large', 'size-descending': 'Size: large to small', 'price-ascending': 'Price: low to high', 'price-descending': 'Price: high to low' };
      status.textContent = sorted.length ? 'Showing ' + (start + 1) + '–' + (start + visible.length) + ' of ' + sorted.length + ' matching products across all brands. ' + sortNames[state.sort] + '.' : 'No published thread gauges match these settings. Try another size or pitch, or select Show all sizes.';
      pager.replaceChildren();
      if (pages > 1) {
        function pageLink(page, label) { const link = element('a', '', label || String(page)); link.href = pageUrl(page).toString(); link.dataset.mqsPage = String(page); link.setAttribute('aria-label', label || 'Page ' + page); return link; }
        if (state.page > 1) pager.appendChild(pageLink(state.page - 1, 'Previous'));
        let previous = 0;
        Array.from(new Set([1, pages, state.page - 1, state.page, state.page + 1])).filter(p => p >= 1 && p <= pages).sort((a, b) => a - b).forEach(page => {
          if (previous && page - previous > 1) pager.appendChild(element('span', '', '…'));
          if (page === state.page) { const current = element('span', '', String(page)); current.setAttribute('aria-current', 'page'); current.setAttribute('aria-label', 'Page ' + page); pager.appendChild(current); }
          else pager.appendChild(pageLink(page)); previous = page;
        });
        if (state.page < pages) pager.appendChild(pageLink(state.page + 1, 'Next'));
      }
      panel.setAttribute('aria-busy', 'false');
    }
    async function load(refresh) {
      const current = ++generation; panel.dataset.mqsLoadState = 'loading'; loaded = false; panel.setAttribute('aria-busy', 'true'); retry.hidden = true; fallback.hidden = true; controls.hidden = true;
      if (native) native.hidden = false;
      grid.replaceChildren(); pager.replaceChildren();
      status.textContent = 'Loading thread-size filters… Standard search results remain available below.';
      try {
        const results = await getCatalog(refresh);
        if (current !== generation || !panel.isConnected) return;
        products = results; intent = intentFor(query, products, seeds); readState(); loaded = true; controls.hidden = false; render();
        if (native) native.hidden = true;
        panel.dataset.mqsLoadState = 'ready';
      } catch (error) {
        if (current !== generation || !panel.isConnected) return;
        loaded = false; controls.hidden = true; grid.replaceChildren(); pager.replaceChildren();
        panel.setAttribute('aria-busy', 'false');
        if (native) native.hidden = false;
        status.textContent = 'Size sorting is temporarily unavailable. Standard search results are shown below.';
        panel.dataset.mqsLoadState = 'standard';
        retry.hidden = false; fallback.hidden = !native;
        if (global.console) global.console.warn('[MQS thread search]', error);
      }
    }
    controls.addEventListener('change', event => {
      if (event.target === sizeSelect) { state.size = sizeSelect.value; state.pitch = ''; }
      else if (event.target === pitchSelect) state.pitch = pitchSelect.value;
      state.kind = kindSelect.value; state.sort = sortSelect.value; state.perPage = Number(pageSelect.value); state.page = 1;
      populateFilters(); render(); updateUrl();
    });
    panel.querySelector('[data-mqs-reset]').addEventListener('click', () => { state.size = ''; state.pitch = ''; state.kind = ''; state.all = true; state.page = 1; populateFilters(); render(); updateUrl(); });
    pager.addEventListener('click', event => {
      const link = event.target.closest('a[data-mqs-page]');
      if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault(); state.page = Number(link.dataset.mqsPage); render(); updateUrl();
      const header = global.document.querySelector('.header'), offset = header ? Math.min(180, header.getBoundingClientRect().height) : 20;
      const reduced = global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches;
      global.scrollTo({ top: Math.max(0, controls.getBoundingClientRect().top + global.scrollY - offset - 18), behavior: reduced ? 'auto' : 'smooth' });
      status.tabIndex = -1; status.focus({ preventScroll: true });
    });
    retry.addEventListener('click', () => load(true));
    fallback.addEventListener('click', () => {
      generation++; if (native) native.hidden = false; panel.hidden = true;
      const url = new URL(global.location.href); url.searchParams.set('mqs_standard', '1');
      global.history.replaceState({}, '', url.toString());
    });
    global.addEventListener('popstate', () => { if (panel.isConnected && loaded) { readState(); render(); } });
    load(false);
  }
  function mountAll(root) { (root || global.document).querySelectorAll('[data-mqs-thread-search]').forEach(mount); }
  const api = { version: VERSION, isThreadQuery, isThreadGauge, parseMetricSize, prepareRecord, intentFor, matchesSize, matchesTerms, sortRecords,
    suggestions, suggestionError, getProducts, getCatalog, mountAll, makeFeedUrl, parseFeed, addPreview }; 
  global.MqsThreadSearch = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (global.document) {
    if (global.document.readyState === 'loading') global.document.addEventListener('DOMContentLoaded', () => mountAll()); else mountAll();
    global.document.addEventListener('shopify:section:load', event => mountAll(event.target));
  }
})(typeof window !== 'undefined' ? window : globalThis);
